import type { EvenementConferencier } from '@mtc/shared';
import { useEffect } from 'react';

/**
 * Overlay popover showing a conférencier's photo, title and bio. Triggered
 * from any chip referencing the speaker (atelier, masterclass, conférence).
 *
 * Behaviour:
 *  - Closes on Escape, on backdrop click, or on the explicit × button.
 *  - Locks page scroll while open (mobile-friendly).
 *  - Renders nothing when `conferencier` is null.
 */
export function ConferencierModal({
  conferencier,
  onClose,
}: {
  conferencier: EvenementConferencier | null;
  onClose: () => void;
}) {
  useEffect(() => {
    if (!conferencier) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [conferencier, onClose]);

  if (!conferencier) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Conférencier — ${conferencier.name}`}
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        background: 'rgba(15,25,22,0.65)',
        backdropFilter: 'blur(4px)',
        display: 'grid',
        placeItems: 'center',
        padding: 16,
        overflowY: 'auto',
        animation: 'mtcFadeIn 0.18s ease',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 18,
          width: '100%',
          maxWidth: 540,
          maxHeight: 'calc(100vh - 32px)',
          overflowY: 'auto',
          boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
          position: 'relative',
        }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Fermer"
          style={{
            position: 'absolute',
            top: 14,
            right: 14,
            width: 36,
            height: 36,
            borderRadius: 999,
            border: 0,
            background: 'rgba(15,25,22,0.06)',
            color: 'var(--mtc-ink, #0F1916)',
            fontSize: 18,
            lineHeight: 1,
            cursor: 'pointer',
            transition: 'background 0.18s ease',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(15,25,22,0.12)')}
          onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(15,25,22,0.06)')}
        >
          ×
        </button>

        <div style={{ padding: '28px 28px 24px' }}>
          <div style={{ display: 'flex', gap: 18, alignItems: 'center', marginBottom: 18 }}>
            {conferencier.photoUrl ? (
              <img
                src={conferencier.photoUrl}
                alt={conferencier.name}
                style={{
                  width: 96,
                  height: 96,
                  borderRadius: '50%',
                  objectFit: 'cover',
                  border: '3px solid var(--mtc-primary, #0B5D3B)',
                  flexShrink: 0,
                }}
              />
            ) : (
              <div
                style={{
                  width: 96,
                  height: 96,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #0B5D3B 0%, #094A30 100%)',
                  color: 'white',
                  display: 'grid',
                  placeItems: 'center',
                  fontSize: 36,
                  fontFamily: 'var(--mtc-font-display)',
                  flexShrink: 0,
                }}
              >
                {(conferencier.name?.[0] ?? '?').toUpperCase()}
              </div>
            )}
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: 11,
                  fontFamily: 'var(--mtc-font-mono)',
                  letterSpacing: '0.15em',
                  textTransform: 'uppercase',
                  color: 'rgba(15,25,22,0.5)',
                  marginBottom: 6,
                }}
              >
                Conférencier
              </div>
              <h3
                style={{
                  fontFamily: 'var(--mtc-font-display)',
                  fontSize: 22,
                  fontWeight: 500,
                  margin: '0 0 4px',
                  letterSpacing: '-0.02em',
                  color: 'var(--mtc-ink, #0F1916)',
                  lineHeight: 1.2,
                }}
              >
                {conferencier.name}
              </h3>
              {conferencier.title && (
                <div style={{ fontSize: 14, color: 'rgba(15,25,22,0.65)' }}>
                  {conferencier.title}
                </div>
              )}
            </div>
          </div>

          {conferencier.bio && (
            <div
              style={{
                fontSize: 14,
                lineHeight: 1.65,
                color: 'rgba(15,25,22,0.82)',
                whiteSpace: 'pre-wrap',
                marginTop: 16,
                paddingTop: 16,
                borderTop: '1px solid rgba(15,25,22,0.08)',
              }}
            >
              {conferencier.bio}
            </div>
          )}

          {conferencier.cvPdfUrl && (
            <div style={{ marginTop: 20 }}>
              <a
                href={conferencier.cvPdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mtc-btn mtc-btn-ghost"
                style={{ fontSize: 13 }}
              >
                📄 Télécharger le CV (PDF) ↗
              </a>
            </div>
          )}
        </div>
      </div>

      <style>{`@keyframes mtcFadeIn { from { opacity: 0 } to { opacity: 1 } }`}</style>
    </div>
  );
}
